import CustomTooltip from '../../shared/CustomTooltip';
import { ArrowBack } from '@mui/icons-material';
import {
    Box,
    Grid,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useDeletePolicyMutation,
    useGetPolicyListQuery,
} from '../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import { CustomTextField } from '../../../layout/styledcomponents/CustomTextField';
import MultiSelectDropdownFilter from '../../../layout/styledcomponents/filters/MultiSelectDropdown';
import UserRole from '../../../shared/AuthRoles';
import ConfirmationModal from '../../shared/ConfirmationModal';
import toast from '../../shared/Toast';
import { Policy } from '../../types/policy';
import DropdownFilter from '../../../layout/styledcomponents/filters/Dropdown';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { ListSection } from '../../../layout/styledcomponents/TooltipStyling';
import { TooltipCancellationContent } from './PolicyTooltipContent';

export const bookingPoliciesContent = [
    {
        title: 'Flexible',
        value: 'flexible',
        items: [
            'Full refund if cancelled 24 hours before check-in.',
            'No refund if cancelled less than 24 hours before check-in/event.',
            'Note: 7DAY retains the guests service fee.',
        ],
    },
    {
        title: 'Fair',
        value: 'fair',
        items: [
            '100% refund if cancelled at least 7 days before check-in/event.',
            '50% refund if cancelled 3-6 days before check-in/event.',
            'No refund if cancelled less than 3 days before check-in/event.',
            'Note: 7DAY retains both host and guests service fees.',
        ],
    },
    {
        title: 'Firm',
        value: 'firm',
        items: [
            '75% refund if cancelled at least 14 days before check-in/event.',
            '50% refund if cancelled 7-13 days before check-in/event.',
            'No refund if cancelled less than 7 days before check-in/event.',
            'Note: 7DAY retains both host and guests service fees.',
        ],
    },
    {
        title: 'Non-Refundable',
        value: 'nonRefundable',
        items: [
            'No refund for any cancellations.',
            'Note: 7DAY retains both host and guests service fees.',
        ],
    },
];

interface FormProps {
    policy?: Policy;
    onSubmit: (data: Policy) => void;
}

const PolicyForm = ({ policy, onSubmit }: FormProps) => {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const loggedInUser = UserRole();

    const theme = useTheme();

    const { data: policies } = useGetPolicyListQuery(loggedInUser?.company);

    const navigate = useNavigate();

    const [deletePolicy] = useDeletePolicyMutation();

    const [name, setName] = useState<string>('');
    const [isDuplicateName, setIsDuplicateName] = useState<boolean>(false);

    const [experienceTypes, setExperienceTypes] = useState<string[]>([
        'property',
        'boat',
        'vehicle',
        'activity',
        'commercial',
    ]);
    const [cancellationType, setCancellationType] = useState<any>('');

    const [description, setDescription] = useState<string>('');
    // delete modal
    const [showModal, setShowModal] = useState<boolean>(false);

    const experienceOptions = [
        { value: 'property', label: 'Property' },
        { value: 'boat', label: 'Boat' },
        { value: 'vehicle', label: 'Vehicle' },
        { value: 'activity', label: 'Activity' },
        { value: 'commercial', label: 'Commercial' },
    ];

    const cancellationOptions = [
        { value: 'flexible', label: 'Flexible' },
        { value: 'fair', label: 'Fair' },
        { value: 'firm', label: 'Firm' },
        { value: 'nonRefundable', label: 'Non-Refundable' },
    ];

    const handleName = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);

        // check if name already exists
        const duplicateName =
            policies?.some(
                (policy: Policy) =>
                    policy?.name.toLowerCase() === e.target.value.toLowerCase(),
            ) && e.target.value.toLowerCase() !== policy?.name.toLowerCase();

        setIsDuplicateName(duplicateName);
    };

    const handleDescription = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDescription(e.target.value);
    };

    const handleDelete = () => {
        deletePolicy([loggedInUser?.company, policy?.id]).then((data: any) => {
            if (data.error) {
                toast.error(`Policy cannot be deleted`);
            } else {
                toast.success(`Policy has been deleted`);
                navigate('/policies');
            }
        });
    };

    const handleSubmit = () => {
        onSubmit({
            name: name,
            moduleTypes: experienceTypes,
            description: description,
            cancellationType: cancellationType,
        });
    };

    useEffect(() => {
        if (policy) {
            setName(policy?.name);
            setExperienceTypes(policy?.moduleTypes);
            setDescription(policy?.description);
            setCancellationType(policy?.cancellationType);
        }
    }, [policy]);

    const TooltipExperienceContent = () => (
        <Box>
            <ListSection
                title='Experience Type'
                items={[
                    'Select the type of experiences in which this policy applies too.',
                    'Note: This does not apply it to all experiences, you will still need to go to the individual experience and apply this policy to it',
                ]}
            />
        </Box>
    );

    const TooltipDescriptionContent = () => (
        <Box>
            <ListSection
                title='Description'
                items={[
                    'Add your terms for this policy so your guests know what they are agreeing too',
                ]}
            />
        </Box>
    );

    return (
        <>
            <Stack spacing={2} width='100%'>
                <Grid container spacing={2}>
                    <Grid item container xs={12} spacing={1}>
                        <Grid item xs={12} lg={2}>
                            <Typography>
                                Name
                                {!name && (
                                    <span style={{ color: 'red' }}>*</span>
                                )}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <CustomTextField
                                color='green'
                                fullWidth
                                value={name}
                                onChange={handleName}
                                error={!!isDuplicateName}
                                helperText={
                                    isDuplicateName && 'Policy already exists'
                                }
                                inputProps={{
                                    maxLength: 19,
                                    style: {
                                        height: '10px',
                                        width: '350px',
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid item container xs={12} spacing={1}>
                        <Grid item xs={12} lg={2}>
                            <Typography>
                                Experience Type
                                <CustomTooltip
                                    title={<TooltipExperienceContent />}
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: (theme) =>
                                                theme.palette.text.primary,
                                            fontSize: '16px',
                                            cursor: 'pointer',
                                        }}
                                    />
                                </CustomTooltip>
                            </Typography>
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <MultiSelectDropdownFilter
                                options={experienceOptions}
                                handleChange={setExperienceTypes}
                                selectedValue={experienceTypes}
                                backgroundColor={theme.palette.neutral.main}
                                border={theme.palette.midGrey.main}
                            />
                        </Grid>
                    </Grid>
                    <Grid item container xs={12} spacing={1}>
                        <Grid item xs={12} lg={2}>
                            <Typography
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                Cancellation Policy Type
                                {!cancellationType && (
                                    <span style={{ color: 'red' }}>*</span>
                                )}
                                <CustomTooltip
                                    title={
                                        <TooltipCancellationContent
                                            content={bookingPoliciesContent}
                                        />
                                    }
                                    placement='right'
                                    slotProps={{
                                        tooltip: {
                                            sx: {
                                                maxHeight: 400,
                                                overflowY: 'auto',
                                            },
                                        },
                                    }}
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: (theme) =>
                                                theme.palette.text.primary,
                                            fontSize: '16px',
                                            cursor: 'pointer',
                                        }}
                                    />
                                </CustomTooltip>
                            </Typography>
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <DropdownFilter
                                id={'cancellationType'}
                                options={cancellationOptions}
                                handleChange={setCancellationType}
                                selectedValue={cancellationType}
                                backgroundColor={theme.palette.neutral.main}
                                border={theme.palette.midGrey.main}
                            />
                        </Grid>
                    </Grid>
                    <Grid item container xs={12} spacing={1}>
                        <Grid item xs={12} lg={2}>
                            <Typography>
                                Description
                                {!description && (
                                    <span style={{ color: 'red' }}>*</span>
                                )}
                                <CustomTooltip
                                    title={<TooltipDescriptionContent />}
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: (theme) =>
                                                theme.palette.text.primary,
                                            fontSize: '16px',
                                            cursor: 'pointer',
                                        }}
                                    />
                                </CustomTooltip>
                            </Typography>
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <CustomTextField
                                color='green'
                                fullWidth
                                multiline
                                rows={8}
                                inputProps={{}}
                                value={description}
                                onChange={handleDescription}
                            />
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        xs={12}
                        display='flex'
                        justifyContent={
                            isMobile || isIpad ? 'flex-end' : 'space-between'
                        }
                    >
                        <CustomButton
                            color='primary'
                            startIcon={<ArrowBack />}
                            sx={{
                                '&:hover': { background: 'none' },
                                fontSize: '12px',
                                ml: '-8px',
                                display: isMobile || isIpad ? 'none' : 'flex', // Hide on mobile
                            }}
                            onClick={() => navigate('/policies')}
                        >
                            Back to policies
                        </CustomButton>
                        <Box display='flex' gap='10px'>
                            {policy && (
                                <CustomButton
                                    color='error'
                                    variant='contained'
                                    onClick={() => setShowModal(true)}
                                >
                                    Delete
                                </CustomButton>
                            )}
                            <CustomButton
                                color='primary'
                                variant='contained'
                                onClick={handleSubmit}
                                disabled={
                                    !name ||
                                    !experienceTypes ||
                                    !description ||
                                    isDuplicateName
                                }
                            >
                                {policy ? 'Update' : 'Save'}
                            </CustomButton>
                        </Box>
                    </Grid>
                </Grid>
            </Stack>
            <ConfirmationModal
                open={showModal}
                onClose={() => setShowModal(false)}
                onChange={handleDelete}
                message={'delete this policy'}
            />
        </>
    );
};

export default PolicyForm;
